<script lang="ts">
    import { getContext, onDestroy, onMount } from 'svelte'
    import type { Writable } from 'svelte/store'

    import Icon from '@iconify/svelte'
    import { orderBy } from 'lodash-es'

    import { cardHeaderStyle } from 'hydria/context'
    import { gettext as _ } from 'hydria/services/gettext'
    import { simpleFetch, type SimpleFetchResult } from 'hydria/services/session'
    import { toLuxon } from 'hydria/utils/time'

    import type { SessionEntry } from './sessions_defs'

    import Date from 'hydria/components/Date.svelte'
    import LoadingPattern from 'hydria/components/LoadingPattern.svelte'

    let stoken = getContext('securityToken') as Writable<string>
    interface Props {
        updInc: Writable<number>
    }

    let { updInc }: Props = $props()

    const unsubs: any[] = []
    onDestroy(() => {
        for (const unsub of unsubs) unsub.unsubscribe != null ? unsub.unsubscribe() : unsub()
    })
    let loading = $state(true)
    let unsupported = $state(false)

    let entries: SessionEntry[] = $state([])

    async function load(initial: boolean = false) {
        let r: SimpleFetchResult
        if (loading && !initial) {
            return
        }

        loading = true
        try {
            r = await simpleFetch('GET', '/json/security/1/sessions', undefined, { headers: { 'x-security-token': $stoken } })
        } catch (e: any) {
            r = e
        }

        if (r.status == 400) {
            unsupported = true
            loading = false
            return
        }

        if (!r || !r.data || !r.data.success) {
            let error = r && r.data && r.data.error ? r.data.error : 'Undefined error'
            console.error('Error loading active sessions:', error)
            return
        }
        loadEntries(r.data)
        loading = false
    }

    function loadEntries(data: any) {
        const lentries: SessionEntry[] = []
        for (const e of data.entries) {
            e.ctime = toLuxon(e.ctime)
            e.mtime = toLuxon(e.mtime)

            if (e.twofa_method == '') e.twofa_method = null

            lentries.push(e)
        }
        entries = orderBy(lentries, [(e) => (e.current ? 1 : 2), (e) => e.mtime], ['asc', 'desc'])
    }

    let closing: boolean = $state(false)
    async function closeOtherSessions() {
        let r: SimpleFetchResult
        if (loading || closing) {
            return
        }

        closing = true
        try {
            r = await simpleFetch('DELETE', '/json/security/1/sessions', undefined, { headers: { 'x-security-token': $stoken } })
        } catch (e: any) {
            r = e
        }

        if (!r || !r.data || !r.data.success) {
            let error = r && r.data && r.data.error ? r.data.error : 'Undefined error'
            console.error('Error closing active sessions:', error)
            return
        }

        loadEntries(r.data)
        closing = false
    }

    onMount(() => {
        load(true)
    })

    function totpText(t: string | null) {
        if (t == 'totp') return $_('Application')
        if (t == 'sms') return $_('Code par SMS')
        if (t == 'email') return $_('Code par courriel')
        if (t == 'recovery') return $_('Code à usage unique')
        if (t != null) return `(? ${t} ?)`
        return '—'
    }

    function methodText(e: SessionEntry) {
        let o: string = ''

        switch (e.login_method) {
            case 'password':
                o += $_('Mot de passe')
                break
            case 'passkey':
                o += $_('Clé d’accès')
                break
            case 'admin':
                o += $_('Admin')
                break
            default:
                o += `? ${e.login_method} ?`
                break
        }

        switch (e.twofa_method) {
            case 'totp':
                o += ' / ' + $_('Application')
                break
            case 'sms':
                o += ' / ' + $_('Code par SMS')
                break
            case 'email':
                o += ' / ' + $_('Code par courriel')
                break
            case 'recovery':
                o += ' / ' + $_('Code à usage unique')
                break
            case undefined:
            case null:
                break
            default:
                o += ` / ? ${e.twofa_method} ?`
                break
        }

        return o
    }

    unsubs.push(
        updInc.subscribe(() => {
            if (!loading) load()
        }),
    )
</script>

<style>
    .session-table {
        display: grid;
        grid-template-columns: 1em 11em 11em 1fr 9.5em 9.5em;
        grid-gap: 0.25rem 0.5rem;
    }
    .uagent {
        display: none;
    }
    @media (min-width: 1440px) {
        .session-table {
            grid-template-columns: 1em 11em 11em 2fr 9.5em 9.5em 3fr;
        }
        .uagent {
            display: initial;
        }
    }
    .h.ind {
        border-bottom: 0;
    }
    .h:not(.ip) {
        grid-row: 1 / 3;
    }
    .hh.ip {
        border-bottom: 1px solid #ddd;
        grid-column: 5 / 7;
        text-align: center;
    }

    .hh {
        grid-row: 1;
    }
    .h {
        grid-row: 2;
    }
    .h,
    .hh {
        font-weight: 500;
        width: 100%;
        border-bottom: 1px solid var(--p-grayish);
        align-self: end; /* or stretch, looks weird */
    }
    .session-table > * {
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: normal;
        word-break: keep-all;
        word-wrap: normal;
    }

    .ip {
        font-feature-settings:
            'ss01' 1,
            'tnum' 1;
        justify-self: center;
    }

    .h.ip {
        text-align: center;
    }
    .ip.ip-same {
        /* color: #aaa; */
        opacity: 0.25;
    }

    .uagent:not(.h) {
        font-size: 0.9em;
    }

    .current {
        color: var(--p-accent-mid);
    }
    /* .current.ip.ip-same {
        color: var(--p-accent-mid);
    } */
</style>

{#if !unsupported}
    <div class="card mb-4">
        <div class={cardHeaderStyle}>
            <span>{$_('Sessions actives')}</span>
        </div>
        <div class="card-body">
            {#if loading}
                <LoadingPattern pattern={[100, 100, '-', 100, 100]} />
            {:else if entries.length < 1}
                <p>{$_('Aucune entrée')}</p>
            {:else}
                <div class="session-table">
                    <div class="h ind"></div>
                    <div class="h stamp">{$_('Création')}</div>
                    <div class="h stamp">{$_('Dernière mise à jour')}</div>
                    <div class="h method">{$_('Méthode')}</div>
                    <div class="hh ip">{$_('Adresse IP')}</div>
                    <div class="h ip">{$_('Création')}</div>
                    <div class="h ip">{$_('Dernière')}</div>
                    <div class="h uagent">{$_('Agent utilisateur')}</div>
                    {#each entries as entry}
                        <div class:current={entry.current} class="ind">
                            {#if entry.current}
                                <span data-tooltip={$_('Session actuelle')}>
                                    <Icon inline icon="mdi:user-access-control" />
                                </span>
                            {/if}
                        </div>
                        <div class:current={entry.current} class="stamp"><Date value={entry.ctime} format="h-table-s" /></div>
                        <div class:current={entry.current} class="stamp"><Date value={entry.mtime} format="h-table-s" /></div>
                        <div class:current={entry.current} class="method">{methodText(entry)}</div>
                        <div class:current={entry.current} class="ip">{entry.c_remote_addr}</div>
                        <div class:current={entry.current} class="ip" class:ip-same={entry.c_remote_addr == entry.last_remote_addr} class:ip-diff={entry.c_remote_addr != entry.last_remote_addr}>{entry.last_remote_addr}</div>
                        <div class:current={entry.current} class="uagent" class:danger={entry.c_user_agent != entry.last_user_agent}>{entry.c_user_agent}</div>
                    {/each}
                </div>
            {/if}
        </div>
        {#if !loading && entries.length}
            <div class="card-footer">
                <button class="btn btn-outline-primary" disabled={closing || entries.filter((e) => !e.current).length < 1} onclick={() => closeOtherSessions()}>
                    {$_('Fermer toutes les sessions')}
                </button>
            </div>
        {/if}
    </div>
{/if}
